import { dao } from '@platform/dao';
import type { IRecordTypeOverrideDoc } from '@platform/dao';
import type { IModuleDefinition, IRecordTypeDefinition } from '@platform/core/types';
import type { IOverrideArg } from './args/IOverrideArg';
import type { IOverridesArg } from './args/IOverridesArg';
import type { ISaveOverrideArg } from './args/ISaveOverrideArg';
import { baseApi, run } from './baseApi';
import { catalogTag } from './catalogTag';
import { mapLimited } from './mapLimited';
import { metadataApi } from './metadataApi';
import { metadataBulkApi } from './metadataBulkApi';
import { moduleTag } from './moduleTag';

/** Скільки перевизначень читати одночасно. */
const PARALLEL = 4;
const overrideTag = ({ ws, rt }: IOverrideArg) => ({ type: 'Override' as const, id: `${ws}:${rt}` });
const keysOf = (keys: string) => keys.split(',').filter(Boolean);
const replaceType = (def: IModuleDefinition, rt: IRecordTypeDefinition) => { def.recordTypes = def.recordTypes.map((r) => (r.key === rt.key ? rt : r)); };

/**
 * Перевизначення типів записів простору (ADR-023, конструктор модулів): патч із версією. Збереження кладе нову версію й патч у кеш (наступна
 * зміна йде з правильним If-Match без повторного читання), злитий тип запису — у кеш визначення модуля простору й у зведення визначень
 * «Налаштувань» (без перечитування всіх модулів); визначення модуля, його типи й каталог простору перечитуються. Скидання (DELETE)
 * перечитує все. Перевизначення кількох типів (не більше 4 запитів одночасно) читають «Налаштування › Форми» — чи простір має власну форму
 * типу (патч із `forms`); їх перечитує будь-яка зміна каталогу простору чи перевизначень цих типів.
 */
export const overrideApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    recordTypeOverride: b.query<IRecordTypeOverrideDoc, IOverrideArg>({
      queryFn: ({ ws, rt }) => run(() => dao.overrides.get(ws, rt)),
      providesTags: (_r, _e, arg) => [overrideTag(arg)],
    }),
    recordTypeOverrides: b.query<Record<string, IRecordTypeOverrideDoc>, IOverridesArg>({
      queryFn: ({ ws, keys }) => run(async () => {
        const docs = await mapLimited(keysOf(keys), PARALLEL, async (rt) => [rt, await dao.overrides.get(ws, rt)] as const);
        return Object.fromEntries(docs);
      }),
      providesTags: (_r, _e, { ws, keys }) => [catalogTag(ws), ...keysOf(keys).map((rt) => overrideTag({ ws, rt }))],
    }),
    saveRecordTypeOverride: b.mutation<IRecordTypeDefinition, ISaveOverrideArg>({
      queryFn: ({ ws, rt, patch, version }) => run(() => dao.overrides.save(ws, rt, patch, version)),
      async onQueryStarted({ ws, rt, module, patch, version }, { dispatch, getState, queryFulfilled }) {
        const { data } = await queryFulfilled.catch(() => ({ data: undefined }));
        if (!data) return;
        dispatch(overrideApi.util.updateQueryData('recordTypeOverride', { ws, rt }, (doc) => ({ ...doc, patch, version: version + 1 })));
        dispatch(metadataApi.util.updateQueryData('getModule', { key: module, workspaceId: ws }, (def) => replaceType(def, data)));
        for (const arg of metadataBulkApi.util.selectCachedArgsForQuery(getState(), 'getModuleDefinitions')) {
          if (typeof arg === 'string' || arg.workspaceId !== ws || !arg.keys.split(',').includes(module)) continue;
          dispatch(metadataBulkApi.util.updateQueryData('getModuleDefinitions', arg, (defs) => { defs.filter((d) => d.module.key === module).forEach((d) => replaceType(d, data)); }));
        }
      },
      invalidatesTags: (r, _e, { ws, module }) => (r ? [moduleTag(ws, module), catalogTag(ws)] : []),
    }),
    resetRecordTypeOverride: b.mutation<void, IOverrideArg & { module: string }>({
      queryFn: ({ ws, rt }) => run(() => dao.overrides.reset(ws, rt)),
      invalidatesTags: (_r, _e, { ws, rt, module }) => [overrideTag({ ws, rt }), moduleTag(ws, module), 'Catalog'],
    }),
  }),
});

export const { useRecordTypeOverrideQuery, useRecordTypeOverridesQuery, useSaveRecordTypeOverrideMutation, useResetRecordTypeOverrideMutation } = overrideApi;
